import { Check } from 'lucide-react';
import { cx } from '../ui/cx.ts';
import { useLocale, useT } from './context.tsx';
import { LOCALE_NATIVE_LABELS, SUPPORTED_LOCALES } from './types.ts';

/**
 * Locale control for page chrome — Field Notes (DESIGN.md §4: paper
 * surfaces separate by hairlines, not by filled tracks). Two options do
 * not earn a dropdown, so this is a flat pair of hairline chips: the
 * active one takes the forest tint the sidebar uses for the current nav
 * item, the inactive one stays a quiet outline.
 *
 * It sits directly on `canvas` (Shell header, auth frame backdrop), so
 * the focus ring uses `ring-offset-canvas` per DESIGN.md §9.
 *
 * Two §9 details this control cannot compromise on, because it is the
 * only route a reader who cannot parse the active locale has out of it:
 * the hit target is a full `h-9` (matching `Button` md / `Input` /
 * `Select`), and the selected state carries a `Check` mark rather than
 * leaning on fill alone — `forest-tint` on `canvas` is a 1.04:1
 * difference, effectively invisible in the header.
 *
 * Label resolution uses `LOCALE_NATIVE_LABELS` so every option renders in
 * its own script regardless of the active locale. Those labels are UI
 * chrome and are intentionally NOT in the dictionaries; only the group's
 * aria-label is translated.
 *
 * The choice persists to `localStorage` in `context.tsx`; this component
 * is pure UI over that setter.
 */
export function LocaleSwitcher(): JSX.Element {
  const { locale, setLocale } = useLocale();
  const t = useT();
  return (
    <div
      role="group"
      aria-label={t('i18n.switcher.aria_label')}
      data-testid="locale-switcher"
      className="inline-flex items-center gap-1"
    >
      {SUPPORTED_LOCALES.map((target) => {
        const isActive = locale === target;
        return (
          <button
            key={target}
            type="button"
            aria-pressed={isActive}
            data-testid={`locale-switcher-${target}`}
            onClick={() => {
              if (!isActive) {
                setLocale(target);
              }
            }}
            className={cx(
              'inline-flex h-9 select-none items-center gap-1.5 rounded border px-2.5 text-caption font-medium transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
              isActive
                ? 'border-forest-line bg-forest-tint text-forest-deep'
                : 'border-line text-ink-mid hover:border-line-strong hover:text-ink',
            )}
          >
            {isActive ? <Check aria-hidden="true" className="h-3.5 w-3.5" /> : null}
            {LOCALE_NATIVE_LABELS[target]}
          </button>
        );
      })}
    </div>
  );
}
